import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, Gift, Coins, Infinity as Inf } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { LandingNav } from "@/components/landing/LandingNav";
import { Footer } from "@/components/Footer";

const DEFAULT = [{ id: "small", clips: 10, amount: 4.99 }, { id: "medium", clips: 30, amount: 11.99 }, { id: "large", clips: 100, amount: 29.99 }];
const COLORS = { free: "#3ad7ff", small: "#2f7bff", medium: "#a746ff", large: "#f472b6" };

const Plan = ({ id, clips, amount, cta }) => {
  const { t, lang } = useI18n();
  const c = COLORS[id];
  const hot = id === "medium";
  const fmt = (n) => n.toLocaleString(lang === "en" ? "en-CA" : "fr-CA", { style: "currency", currency: "CAD" });
  return (
    <div className={`${hot ? "cx-neon-frame" : "cx-plan-frame"} rounded-3xl p-[1.5px]`} style={{ "--c": c }} data-testid={`tarif-plan-${id}`}>
      <div className="relative h-full rounded-3xl bg-[#0c0d1c] p-7 flex flex-col overflow-hidden">
        <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full blur-[80px] pointer-events-none" style={{ background: `${c}33` }} />
        {hot && <span className="absolute top-5 right-5 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] text-white bg-gradient-to-r from-[#a746ff] to-[#2f7bff]" data-testid="tarif-popular-badge">{t("tf.popular")}</span>}
        <div className="cx-label" style={{ color: c }}>{t(`tf.name_${id}`)}</div>
        <div className="relative mt-4 flex items-end gap-2">
          <span className="font-display text-6xl font-black text-white leading-none" data-testid={`tarif-clips-${id}`}>{clips}</span>
          <span className="text-slate-400 pb-1.5">{t(id === "free" ? "tf.per_week" : "tf.clips")}</span>
        </div>
        <div className="mt-5 font-display text-2xl font-bold" style={{ color: c }} data-testid={`tarif-price-${id}`}>{amount ? fmt(amount) : t("tf.free_price")}</div>
        <p className="text-xs text-slate-500 mt-1 font-mono">{amount ? `${fmt(amount / clips)} / clip · ${t("tf.once")}` : t("tf.free_note")}</p>
        <ul className="mt-6 space-y-2.5 flex-1">
          {[1, 2, 3].map((n) => <li key={n} className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: c }} />{t(`tf.${id === "free" ? "free" : "paid"}_f${n}`)}</li>)}
        </ul>
        <Link to={cta} data-testid={`tarif-cta-${id}`} className={`mt-7 rounded-full py-3 text-sm font-semibold text-center ${hot ? "cx-btn" : "cx-ghost text-white"}`}>{t(id === "free" ? "tf.cta_free" : "tf.cta_buy")}</Link>
      </div>
    </div>
  );
};

const HOW = [["Sparkles", Sparkles], ["Coins", Coins], ["Inf", Inf], ["Gift", Gift]];

export default function Tarifs() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [packs, setPacks] = useState(DEFAULT);
  useEffect(() => { api.get("/credits/packs").then((r) => r.data?.length && setPacks(r.data)).catch(() => {}); }, []);
  const buy = user ? "/credits" : "/login?mode=register";
  return (
    <div className="min-h-screen bg-[#04050a] text-slate-200" data-testid="tarifs-page">
      <LandingNav />
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-x-0 top-20 h-[420px] bg-[#a746ff]/[0.07] blur-[130px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="cx-label">{t("tf.label")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3" data-testid="tarifs-title">{t("tf.title")}</h1>
          <p className="text-slate-400 mt-5 max-w-2xl text-sm sm:text-base leading-relaxed" data-testid="tarifs-how-text">{t("tf.how")}</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {HOW.map(([k, Icon], i) => <span key={k} className="cx-fx-pill rounded-full px-3.5 py-1.5 text-xs flex items-center gap-1.5" data-testid={`tarifs-point-${i}`}><Icon className="w-3.5 h-3.5 text-[#e9c6ff]" />{t(`tf.pt${i + 1}`)}</span>)}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            <Plan id="free" clips={3} amount={0} cta={user ? "/dashboard" : "/login?mode=register"} />
            {packs.map((p) => <Plan key={p.id} {...p} cta={buy} />)}
          </div>
          <p className="text-xs text-slate-500 mt-8 text-center" data-testid="tarifs-tax-note">{t("tf.tax")}</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
